import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calculator, Menu, X, FolderOpen, LogIn, LogOut, CreditCard, BarChart3, User, ShoppingCart, Package
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { itemCount, setCartOpen } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Inicio", path: "/" },
    { name: "Servicios", path: "/servicios" },
    { name: "Tienda", path: "/tienda" },
    { name: "Fulfillment", path: "/fulfillment" },
    { name: "Guías Prepago", path: "/guias-prepago" },
    { name: "Contacto", path: "/contacto" },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav shadow-sm" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="https://customer-assets-jt897jd0.emergentagent.net/job_todo-carton-refresh/artifacts/1yggjjc0_IMG_3562.jpeg" alt="Todo de Cartón" className="h-11 w-auto object-contain" />
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                className={`font-medium transition-colors duration-200 text-sm ${
                  location.pathname === link.path
                    ? "text-[#2E7D32]"
                    : "text-stone-600 hover:text-[#2E7D32]"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            {/* Cart Button */}
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 text-stone-600 hover:text-[#F57C00] transition-colors"
              data-testid="nav-cart-btn"
            >
              <ShoppingCart size={20} />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#F57C00] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {itemCount > 9 ? "9+" : itemCount}
                </span>
              )}
            </button>

            {user ? (
              <div className="flex items-center gap-3">
                {user.role === "admin" && (
                  <Link to="/admin" className="text-[#2E7D32] font-medium text-sm hover:underline" data-testid="nav-admin">
                    Admin
                  </Link>
                )}
                <Link to="/mis-pedidos" className="text-[#F57C00] font-medium text-sm hover:underline flex items-center gap-1" data-testid="nav-mis-pedidos">
                  <Package size={16} />
                  Pedidos
                </Link>
                <Link to="/mis-guias" className="text-stone-600 hover:text-[#2E7D32] flex items-center gap-1 text-sm" data-testid="nav-mis-guias">
                  <CreditCard size={16} />
                </Link>
                <Link to="/mi-archivo" className="text-stone-600 hover:text-[#2E7D32] flex items-center gap-1 text-sm" data-testid="nav-mi-archivo">
                  <FolderOpen size={16} />
                </Link>
                <Link to="/analitica" className="text-stone-600 hover:text-[#2E7D32] flex items-center gap-1 text-sm" data-testid="nav-analitica">
                  <BarChart3 size={16} />
                </Link>
                <Link to="/perfil" className="text-stone-600 hover:text-[#2E7D32] flex items-center gap-1 text-sm" data-testid="nav-perfil">
                  <User size={16} />
                </Link>
                <button onClick={logout} className="text-stone-500 hover:text-stone-700 flex items-center gap-1 text-sm" data-testid="nav-logout">
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <Link to="/login" className="text-[#2E7D32] font-medium text-sm hover:underline flex items-center gap-1" data-testid="nav-login">
                <LogIn size={16} />
                Ingresar
              </Link>
            )}
            <Link to="/cotizar" data-testid="nav-cotizar-btn" className="btn-primary flex items-center gap-2 text-sm">
              <Calculator size={16} />
              Cotizar
            </Link>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 text-stone-600"
              data-testid="mobile-cart-btn"
            >
              <ShoppingCart size={22} />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#F57C00] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {itemCount > 9 ? "9+" : itemCount}
                </span>
              )}
            </button>
            <button
              className="p-2"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="block py-2 font-medium text-stone-700 hover:text-[#2E7D32]"
                >
                  {link.name}
                </Link>
              ))}
              {user && (
                <>
                  <Link to="/mis-pedidos" onClick={() => setIsOpen(false)} className="block py-2 font-medium text-[#F57C00]">
                    Mis Pedidos
                  </Link>
                  <Link to="/mis-guias" onClick={() => setIsOpen(false)} className="block py-2 font-medium text-stone-700">
                    Mis Guías
                  </Link>
                  <Link to="/mi-archivo" onClick={() => setIsOpen(false)} className="block py-2 font-medium text-stone-700">
                    Mi Archivo
                  </Link>
                </>
              )}
              <Link
                to="/cotizar"
                onClick={() => setIsOpen(false)}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Calculator size={18} />
                Cotizar Envío
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
